import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';
import { mediaUrl } from '../../lib/mediaUrl';

const API = `${API_BASE}/api`;

export default function ProgrammeThumbnailCard({ programmeId, thumbnailUrl, headers, onChange }) {
  const [url, setUrl] = useState(thumbnailUrl || null);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const apply = (next) => { setUrl(next); onChange?.(next); };

  const generate = async () => {
    setBusy('generate');
    try {
      const r = await axios.post(`${API}/programmes/${programmeId}/thumbnail/generate`, {}, { headers, timeout: 120000 });
      apply(r.data.thumbnail_url);
      toast.success('Thumbnail generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(null); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/programmes/${programmeId}/thumbnail/upload`, fd, { headers });
      apply(r.data.thumbnail_url);
      toast.success('Thumbnail uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = async () => {
    setBusy('remove');
    try {
      await axios.delete(`${API}/programmes/${programmeId}/thumbnail`, { headers });
      apply(null);
    } catch (e) {
      toast.error('Could not remove thumbnail');
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4 flex flex-col md:flex-row gap-5" data-testid="programme-thumbnail-card">
      <div className="relative w-full md:w-80 aspect-video rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 shrink-0">
        {url ? <img src={mediaUrl(url)} alt="Programme thumbnail" className="w-full h-full object-cover" data-testid="programme-thumbnail-image" />
          : <div className="h-full flex flex-col items-center justify-center text-zinc-600"><ImagePlus className="h-8 w-8 mb-2" /><span className="text-xs">No thumbnail yet</span></div>}
        {busy === 'generate' && <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-amber-300 text-xs gap-2"><Loader2 className="h-6 w-6 animate-spin" />NAVI is illustrating…</div>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">Programme thumbnail</p>
        <p className="text-xs text-zinc-500 mt-1 mb-4">Shown on programme cards and behind the learner programme header. Use a 16:9 image (JPG, PNG or WebP, up to 8 MB), or let NAVI create one from the programme name and description.</p>
        {!programmeId ? (
          <p className="text-xs text-amber-300" data-testid="programme-thumbnail-save-first">Save the programme first. A thumbnail will be generated automatically.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={generate} disabled={!!busy} className="bg-amber-400 hover:bg-amber-300 text-black" data-testid="programme-thumbnail-generate-btn">
              {busy === 'generate' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{url ? 'Regenerate with AI' : 'Generate with AI'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy} className="border-zinc-700 text-zinc-200" data-testid="programme-thumbnail-upload-btn">
              {busy === 'upload' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload
            </Button>
            {url && (
              <Button size="sm" variant="ghost" onClick={remove} disabled={!!busy} className="text-zinc-400 hover:text-rose-300" data-testid="programme-thumbnail-remove-btn">
                <Trash2 className="h-4 w-4 mr-2" />Remove
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="programme-thumbnail-file-input" />
          </div>
        )}
      </div>
    </div>
  );
}
